import clsx from 'clsx'
import { Fragment } from 'react'

import { FvButton, Icon } from '@fv/client-components'
import { militaryToStandardTime, parseQueryString } from '@fv/client-core'

import CustomFilterForm from '@/components/CustomFilterForm/CustomFilterForm'
import { DividedContent } from '@/components/DividedContent'
import { SearchField } from '@/components/inputs/SearchField'
import { List } from '@/components/List'
import { Pinline } from '@/components/Pinline'
import { ShipperSlider } from '@/components/ShipperSlider'
import { ShipperTooltip } from '@/components/ShipperTooltip'
import {
  getFilterQueryData,
  isSavedFilterGroup,
  type SavedFilter,
  type SavedFilterGroup,
} from '@/types/SavedFilter'

import { ShipperOnly } from '../auth/HasRole'
import { ExpanderButton } from './ExpanderButton'
import { FilterDescription } from './FilterDescription'
import { FilterItem } from './FilterItem'
import { useFilterControls } from './hooks'
import {
  useShipmentFilterActions,
  useShipmentFilterListContext,
} from './ShipmentFilterListProvider'
import type { FilterControlProps, FilterPanelDrawerAt } from './types'

const panelSurfaceClassNames =
  'flex min-h-0 flex-col overflow-hidden border-r border-fv-blue-150 bg-fv-blue-50 pt-6 [&_hr]:bg-fv-blue-150'

const responsiveDrawerClassNames = {
  b1300: {
    position:
      'b1300:fixed b1300:inset-y-0 b1300:left-0 b1300:z-[100] b1300:w-[22rem]',
    open: 'b1300:translate-x-0',
    closed: 'b1300:-translate-x-full',
    animation: 'b1300:transition-transform b1300:duration-200 b1300:ease-in',
  },
  b1400: {
    position:
      'b1400:fixed b1400:inset-y-0 b1400:left-0 b1400:z-[100] b1400:w-[22rem]',
    open: 'b1400:translate-x-0',
    closed: 'b1400:-translate-x-full',
    animation: 'b1400:transition-transform b1400:duration-200 b1400:ease-in',
  },
} as const

const createDrawerClassNames = (
  drawerAt: FilterPanelDrawerAt | undefined,
  isOpen: boolean,
) => {
  const drawerTransformClassNames = isOpen
    ? 'translate-x-0'
    : '-translate-x-full'
  const drawerClassNames = clsx(
    'fixed inset-y-0 left-0 z-[100] flex min-h-0 w-[22rem] flex-col transition-transform duration-200 ease-in',
    drawerTransformClassNames,
    panelSurfaceClassNames,
  )

  if (!drawerAt) {
    return clsx('relative flex min-h-0 flex-1 flex-col', panelSurfaceClassNames)
  }

  if (drawerAt === 'always') return drawerClassNames

  const breakpointClassNames = responsiveDrawerClassNames[drawerAt]

  const responsiveTransformClassNames = isOpen
    ? breakpointClassNames.open
    : breakpointClassNames.closed

  return clsx(
    'relative flex min-h-0 flex-1 flex-col',
    breakpointClassNames.position,
    responsiveTransformClassNames,
    breakpointClassNames.animation,
    panelSurfaceClassNames,
  )
}

export const ShipmentFilterPanel = ({
  className,
  drawerAt,
  onScheduleOpen,
  onChange,
}: FilterControlProps) => {
  const {
    search,
    isCustomFilter,
    filters,
    canSearch,
    searchParams,
    activeFilter,
    defaultFilter,
    setSearch,
  } = useFilterControls()
  const { listType, editingFilter, isCreating, forcePanelOpen } =
    useShipmentFilterListContext(s => s)
  const { setEditingFilter, setIsCreating, setForcePanelOpen } =
    useShipmentFilterActions()
  const showForm = isCreating || !!editingFilter

  const theFilter = activeFilter ?? defaultFilter
  const activeFilterTitle = isCustomFilter
    ? 'Custom filter'
    : (theFilter?.fullLabel ?? theFilter.name)

  const activeId = isCustomFilter ? undefined : theFilter?._id

  const handleFilterSelect = (filter?: SavedFilter | SavedFilterGroup) => {
    onChange?.({
      ...(filter && getFilterQueryData(filter)),
    })
  }

  const isSchedulableList = listType === 'readyCompletedList'
  const scheduledReport = theFilter?.scheduledReports?.[0]
  const scheduledTime =
    isSchedulableList && scheduledReport && scheduledReport.enabled
      ? militaryToStandardTime(scheduledReport.time)
      : undefined
  const canSchedule = isSchedulableList && !theFilter?.isLocked

  return (
    <div
      className={clsx(
        'relative flex h-full min-h-0 min-w-0 flex-col',
        className,
      )}
    >
      <div
        className={clsx(
          createDrawerClassNames(drawerAt, forcePanelOpen),
          className,
        )}
      >
        <div className="px-4">
          <div className="flex items-center mb-3">
            <span className="truncate" title={activeFilterTitle}>
              <Icon icon="list" className="text-fv-gray-dark" />
              <span>{activeFilterTitle}</span>
            </span>
            {!isCustomFilter && canSchedule && (
              <ShipperOnly permissions={['load-exporter']}>
                <Pinline dotted flex className="mx-2 !border-fv-blue-300" />
                <div className="relative">
                  <ShipperTooltip
                    label={
                      scheduledTime
                        ? `'${activeFilterTitle}' report will be sent ${scheduledReport?.frequency} via email at ${scheduledTime}`
                        : 'Schedule a daily report'
                    }
                  >
                    <FvButton
                      icon={scheduledTime ? 'paper-plane' : 'clock'}
                      transform="shrink-2"
                      className="flex items-center"
                      onClick={onScheduleOpen}
                    >
                      {scheduledTime && (
                        <span className="-ml-1 text-xs">{scheduledTime}</span>
                      )}
                    </FvButton>
                  </ShipperTooltip>
                </div>
              </ShipperOnly>
            )}

            {isCustomFilter && (
              <>
                <Pinline dotted flex className="mx-2 !border-fv-blue-300" />
                <DividedContent condensed theme="blue">
                  {!activeFilter && (
                    <div>
                      <FvButton
                        title="Edit filter"
                        icon="pen"
                        onClick={() => {
                          setEditingFilter({
                            queryString: searchParams.toString(),
                          })
                        }}
                      />
                    </div>
                  )}
                  <div className="last:pr-0">
                    <FvButton
                      title="Clear filter"
                      icon="times"
                      iconClass="text-fv-orange"
                      onClick={() => handleFilterSelect(defaultFilter)}
                    />
                  </div>
                </DividedContent>
              </>
            )}
          </div>

          <FilterDescription isCustomFilter={isCustomFilter} />

          {canSearch && (
            <SearchField
              initialValue={search}
              onChange={search => setSearch(search)}
            />
          )}
        </div>

        <div className="relative flex-1 basis-full overflow-auto">
          <List className="overflow-auto pb-6 border-fv-blue-150 border-t">
            {filters.map(f => (
              <Fragment key={f._id}>
                <FilterItem
                  onEdit={() => setEditingFilter(f)}
                  filter={f}
                  isActive={f._id === activeId}
                  key={f._id}
                  onSelect={() => handleFilterSelect(f)}
                  onRemoved={() => handleFilterSelect(undefined)}
                  className={clsx({
                    'bg-white': isSavedFilterGroup(f) && f._id !== activeId,
                  })}
                />
                {isSavedFilterGroup(f) &&
                  f.children.map(c => (
                    <FilterItem
                      onEdit={() => setEditingFilter(c)}
                      onRemoved={() => handleFilterSelect(undefined)}
                      filter={c}
                      isNested
                      isActive={c._id === activeId}
                      key={c._id}
                      onSelect={() => handleFilterSelect(c)}
                      className="b1450:border-none"
                    />
                  ))}
              </Fragment>
            ))}
          </List>
        </div>

        <ExpanderButton
          onClick={() => {
            setIsCreating(!isCreating)
          }}
        >
          {!editingFilter && <span>Create a custom filter</span>}
          {editingFilter && (
            <span className="truncate">
              Editing / {editingFilter.name ?? 'Custom'} filter
            </span>
          )}
        </ExpanderButton>
      </div>

      <ShipperSlider
        isOpen={showForm}
        onClose={() => {
          setIsCreating(false)
          setEditingFilter(undefined)
        }}
        width="58rem"
        position="left"
        flush
      >
        <CustomFilterForm
          onClose={() => {
            setIsCreating(false)
            setEditingFilter(undefined)
          }}
          editing={editingFilter}
          onFilter={(qs: string) => {
            setForcePanelOpen(false)
            setIsCreating(false)
            setEditingFilter(undefined)
            onChange?.(parseQueryString(qs))
          }}
        />
      </ShipperSlider>

      {forcePanelOpen && (
        <div
          className="fixed left-0 top-0 z-[99] h-screen w-screen bg-black/60"
          onClick={() => {
            setForcePanelOpen(false)
            setIsCreating(false)
            setEditingFilter(undefined)
          }}
        />
      )}
    </div>
  )
}
